import React, { useCallback, useEffect, useState } from 'react';
import { CalendarX2, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Number(n || 0).toLocaleString('en', { maximumFractionDigits: 0 })}`;
const rateCls = (p) => (p >= 50 ? 'text-rose-700' : p >= 30 ? 'text-amber-700' : 'text-emerald-700');

const RateTable = ({ title, rows, keyLabel, testId }) => {
  const list = rows || [];
  const max = Math.max(1, ...list.map((r) => r.rate_pct || 0));
  const trs = list.map((r) => (
    <tr key={r.key} className="border-t" data-testid={`${testId}-row-${r.key}`}>
      <td className="py-1 pr-2 font-medium text-slate-800 whitespace-nowrap">{r.key}</td>
      <td className="py-1 pr-2 text-right">{r.bookings}</td>
      <td className="py-1 pr-2 text-right text-rose-600">{r.cancelled}</td>
      <td className="py-1 pr-2 w-28">
        <div className="flex items-center gap-1.5"><div className="h-1.5 flex-1 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${Math.min(100, (r.rate_pct / max) * 100)}%` }} /></div><span className={`text-[11px] font-semibold w-11 text-right ${rateCls(r.rate_pct)}`}>{r.rate_pct}%</span></div>
      </td>
      <td className="py-1 text-right text-slate-500">{eur(r.lost_eur)}</td>
    </tr>
  ));
  return (
    <div className="bg-slate-50 rounded-lg border p-3" data-testid={testId}>
      <p className="text-xs font-semibold text-slate-700 mb-1">{title}</p>
      <table className="w-full text-xs"><thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="pb-1">{keyLabel}</th><th className="text-right pr-2">Bookings</th><th className="text-right pr-2">Cancelled</th><th className="pr-2">Rate</th><th className="text-right">Lost</th></tr></thead><tbody>{trs}</tbody></table>
      {!list.length && <p className="text-[11px] text-slate-400 py-2">No data</p>}
    </div>
  );
};

const CancellationCard = () => {
  const [year, setYear] = useState('');
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { setD(await apiCall(`/api/admin/beds24/cancellations${year ? `?year=${year}` : ''}`)); } catch (e) { setErr(e.message); }
    setBusy(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const yearOpts = (d?.years || []).map((y) => <option key={y} value={y}>{y}</option>);
  const months = (d?.by_month || []).filter((m) => m.bookings >= 3);
  const timing = (d?.cancel_timing || []).map((t) => <span key={t.key} className="text-[11px] bg-white border rounded-full px-2 py-0.5" data-testid={`cancel-timing-${t.key}`}><b>{t.cancelled}</b> {t.key}</span>);
  const t = d?.total;

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="cancellation-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarX2 className="w-4 h-4 text-rose-600" /> Cancellation insights</h3>
          <p className="text-xs text-slate-500 mt-1">From the full Beds24 archive (every booking since the start, blocks and inquiries excluded). Lead time = days between booking and arrival. "Lost" = value of cancelled bookings.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="cancel-year"><option value="">All years</option>{yearOpts}</select>
          <button type="button" onClick={load} disabled={busy} className="border rounded-lg p-1.5 hover:bg-slate-50" data-testid="cancel-refresh"><RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      {err && <p className="text-xs text-rose-600" data-testid="cancel-error">{err}</p>}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cancel-kpis">
          <div className="border rounded-lg p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Bookings</p><p className="text-2xl font-bold text-slate-900" data-testid="cancel-kpi-bookings">{t.bookings}</p></div>
          <div className="border rounded-lg p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Cancelled</p><p className="text-2xl font-bold text-rose-600" data-testid="cancel-kpi-cancelled">{t.cancelled}</p></div>
          <div className="border rounded-lg p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Cancellation rate</p><p className={`text-2xl font-bold ${rateCls(t.rate_pct)}`} data-testid="cancel-kpi-rate">{t.rate_pct}%</p></div>
          <div className="border rounded-lg p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Lost vs kept</p><p className="text-lg font-bold text-slate-900"><span className="text-rose-600">{eur(t.lost_eur)}</span> <span className="text-slate-400 text-sm">/ {eur(t.kept_eur)}</span></p></div>
        </div>
      )}
      {d && (
        <div className="grid md:grid-cols-2 gap-3">
          <RateTable title="By channel" rows={d.by_channel} keyLabel="Channel" testId="cancel-by-channel" />
          <RateTable title="By lead time (booked → arrival)" rows={d.by_lead_time} keyLabel="Lead time" testId="cancel-by-lead" />
          <RateTable title="By arrival month" rows={months} keyLabel="Month" testId="cancel-by-month" />
          <div className="space-y-3">
            <RateTable title="By arrival year" rows={d.by_year} keyLabel="Year" testId="cancel-by-year" />
            <div className="bg-slate-50 rounded-lg border p-3" data-testid="cancel-timing">
              <p className="text-xs font-semibold text-slate-700 mb-1">When do guests cancel? (before arrival)</p>
              <div className="flex flex-wrap gap-1.5">{timing}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancellationCard;
